import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, ArrowRight, ShieldCheck, Minus, Plus } from "lucide-react";

const PLAN_INFO = {
  starter: {
    name: "Starter",
    pricePerSeat: 0,
    symbol: "£",
    maxSeats: 1,
    sub: "per month",
    features: ["1 active project", "1 team member", "Kanban board", "Project wiki", "Community support"],
    tier: "free",
    badge: null,
    trialDays: 0,
  },
  pro: {
    name: "Pro",
    pricePerSeat: 9.99,
    symbol: "£",
    maxSeats: 10,
    sub: "per seat / month",
    features: ["Up to 3 projects", "Up to 10 seats", "Gantt timeline & sprints", "AI project assistant", "Time & expense tracking", "Deals CRM pipeline", "Email support"],
    tier: "pro",
    badge: null,
    trialDays: 7,
  },
  business: {
    name: "Business",
    pricePerSeat: 24.99,
    symbol: "£",
    maxSeats: 30,
    sub: "per seat / month",
    features: ["Up to 30 seats", "Unlimited projects", "Budget tracking & burndown", "Xero & HubSpot integrations", "Automated milestone invoicing", "Compliance document vault", "ML margin forecasting", "Priority support"],
    tier: "business",
    badge: "Most popular",
    trialDays: 7,
  },
};

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const plan = params.get("plan") || "pro";
  const info = PLAN_INFO[plan] || PLAN_INFO.pro;

  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isFree = info.pricePerSeat === 0;
  const total = (info.pricePerSeat * seats).toFixed(2).replace(/\.00$/, "");

  const changeSeats = (delta) => {
    setSeats((s) => Math.min(info.maxSeats, Math.max(1, s + delta)));
  };

  const startCheckout = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/payments/checkout", {
        tier: info.tier,
        currency: "GBP",
        origin_url: window.location.origin,
        seats,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("Could not create checkout session. Please try again.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Payment setup failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/auth?mode=signup&next=/checkout?plan=${plan}`);
    }
  }, [authLoading, user, navigate, plan]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-16 px-4">
      <div className="fixed top-0 left-0 w-full h-1 bg-violet-600" />

      <div className="w-full max-w-md">
        <Link to="/#pricing" className="text-xs text-gray-400 hover:text-gray-700 mb-8 inline-flex items-center gap-1 transition-colors">
          ← Back to pricing
        </Link>

        <div className="bg-white border border-gray-100 rounded-2xl shadow-lg shadow-gray-100 p-10">
          {info.badge && (
            <span className="inline-block mb-4 px-3 py-1 bg-amber-400 text-amber-900 text-[11px] font-bold rounded-full">
              {info.badge}
            </span>
          )}
          <h1 className="text-2xl font-black text-gray-900 mb-1">{info.name} Plan</h1>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-4xl font-black text-gray-900">{isFree ? "Free" : `${info.symbol}${info.pricePerSeat}`}</span>
            {!isFree && <span className="text-sm text-gray-400 mb-1.5">/ seat / mo</span>}
          </div>
          <p className="text-xs text-gray-400 mb-6">{info.sub}</p>

          {/* Seat picker */}
          {!isFree && (
            <div className="bg-gray-50 border border-gray-100 rounded-lg px-5 py-4 mb-6">
              <div className="text-xs text-gray-400 mb-3 font-semibold uppercase tracking-wide">Number of seats</div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => changeSeats(-1)}
                    disabled={seats <= 1}
                    className={`w-8 h-8 rounded-md flex items-center justify-center font-bold transition-colors ${
                      seats <= 1 ? "bg-gray-100 text-gray-300 cursor-not-allowed" : "bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer"
                    }`}
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-2xl font-black text-gray-900 min-w-[40px] text-center">{seats}</span>
                  <button
                    onClick={() => changeSeats(1)}
                    disabled={seats >= info.maxSeats}
                    className={`w-8 h-8 rounded-md flex items-center justify-center font-bold transition-colors ${
                      seats >= info.maxSeats ? "bg-gray-100 text-gray-300 cursor-not-allowed" : "bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer"
                    }`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-gray-400 mb-0.5">Total per month</div>
                  <div className="text-2xl font-black text-violet-600">{info.symbol}{total}</div>
                  <div className="text-[11px] text-gray-400">{seats} seat{seats !== 1 ? "s" : ""} × {info.symbol}{info.pricePerSeat}</div>
                </div>
              </div>
              <div className="mt-2.5 text-[11px] text-gray-400">
                Max {info.maxSeats} seats on {info.name} plan
              </div>
            </div>
          )}

          <ul className="list-none p-0 m-0 mb-8 flex flex-col gap-2.5">
            {info.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle2 className="w-4 h-4 text-violet-500 shrink-0" />
                {f}
              </li>
            ))}
          </ul>

          {error && <p className="text-red-500 text-[13px] mb-4">{error}</p>}

          <button
            onClick={startCheckout}
            disabled={busy}
            className={`w-full h-12 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
              busy ? "bg-violet-300 text-white cursor-not-allowed" : "bg-violet-600 text-white hover:bg-violet-700 cursor-pointer"
            }`}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isFree ? (
              <>Get started free <ArrowRight className="w-4 h-4" /></>
            ) : info.trialDays ? (
              <>Start {info.trialDays}-day free trial <ArrowRight className="w-4 h-4" /></>
            ) : (
              <>Pay {info.symbol}{total}/mo · {seats} seat{seats !== 1 ? "s" : ""} <ArrowRight className="w-4 h-4" /></>
            )}
          </button>

          {isFree ? (
            <p className="text-center text-xs text-gray-400 mt-3">
              No card required. Upgrade any time as your team grows.
            </p>
          ) : info.trialDays ? (
            <p className="text-center text-xs text-gray-400 mt-3">
              No charge today. After {info.trialDays} days you'll be billed {info.symbol}{total}/mo · {seats} seat{seats !== 1 ? "s" : ""} — cancel any time before then.
            </p>
          ) : null}

          {!isFree && (
            <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-gray-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              Secured by Stripe · Cancel any time
            </div>
          )}

          <p className="text-center text-xs text-gray-400 mt-3">
            Signed in as <span className="text-gray-600">{user.email}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
